import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { initNativePush } from '../utils/nativePush';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Always include httpOnly auth cookie on every request.
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Bearer header is a fallback for the WebSocket and any legacy callers.
  // The primary auth path is the httpOnly cookie set by the backend.
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check auth status on mount — cookie carries the session, so we ask the
  // backend who we are. Falls back gracefully if neither cookie nor token exist.
  useEffect(() => {
    let cancelled = false;
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        if (!cancelled) setUser(response.data);
      } catch (error) {
        if (!cancelled && token) {
          // Token exists locally but server rejected — clear it.
          localStorage.removeItem('token');
          setToken(null);
          delete axios.defaults.headers.common['Authorization'];
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    checkAuth();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Register native push token (iOS/Android app) once the user is signed in.
  useEffect(() => {
    if (user) initNativePush();
  }, [user]);

  const register = async (username, email, password) => {
    const response = await axios.post(`${API}/auth/register`, {
      username,
      email,
      password
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, {
      username,
      password
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    // Best-effort cookie clear on the server; never block UI on this.
    try { await axios.post(`${API}/auth/logout`); } catch (_) { /* ignore */ }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateUser = (updatedData) => {
    setUser(prev => ({ ...prev, ...updatedData }));
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      register,
      login,
      logout,
      updateUser,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
